// src/components/sql/AddSnapVariant.jsx
import { useState, useEffect } from "react";
import axios from "axios";
import "../../App.css";

const API_BASE = (process.env.REACT_APP_API_URL || "").replace(/\/?$/, "/");

export default function AddSnapVariant({ onSaved, userId }) {
  const [cards, setCards] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);

  const [formData, setFormData] = useState({
    cardId: null,
    cardName: "",
    cardHTML: "",
    releaseDate: "",
    chanceOfGetting: "",
  });

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Load all cards for the selector
  useEffect(() => {
    let mounted = true;
    axios
      .get(`${API_BASE}api/data/snapAllCards`)
      .then((res) => {
        if (mounted) setCards(res.data || []);
      })
      .catch((err) => {
        console.error("Error fetching cards:", err);
      });
    return () => {
      mounted = false;
    };
  }, []);

  // Filter cards by search term
  const filteredCards = cards.filter((c) =>
    (c.cardname || c.cardName || "")
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  const handleInputChange = (e) => {
    const { value } = e.target;
    setSearchTerm(value);
    setFormData((prev) => ({ ...prev, cardName: value, cardId: null }));
    setShowDropdown(true);
  };

  const handleCardSelect = (card) => {
    const name = card.cardname || card.cardName;
    setFormData((prev) => ({
      ...prev,
      cardId: card.id,
      cardName: name,
    }));
    setSearchTerm(name);
    setShowDropdown(false);
  };

  const handleAdd = async () => {
    setError("");

    if (!formData.cardId) {
      setError("Please select a card.");
      return;
    }
    if (!formData.cardHTML?.trim()) {
      setError("Variant Image URL (cardHTML) is required.");
      return;
    }
    if (formData.chanceOfGetting === "" || isNaN(Number(formData.chanceOfGetting))) {
      setError("Chance of Getting must be a number.");
      return;
    }

    try {
      setSaving(true);

      await axios.post(`${API_BASE}api/addSnapVariant`, {
        cardId: Number(formData.cardId),
        cardHTML: formData.cardHTML.trim(),
        // Send date string or null (backend handles null)
        releaseDate: formData.releaseDate || null,
        chanceOfGetting: Number(formData.chanceOfGetting),
        userId: Number.isFinite(userId) ? userId : null,
      });

      // Let parent refetch with the correct list endpoint & params
      if (typeof onSaved === "function") onSaved();

      // Reset the form
      setFormData({
        cardId: null,
        cardName: "",
        cardHTML: "",
        releaseDate: "",
        chanceOfGetting: "",
      });
      setSearchTerm("");
      setShowDropdown(false);
    } catch (err) {
      console.error("Error adding snap variant:", err);
      setError("Failed to add variant.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="styled-info-modal">
      <h2 className="modal-title" style={{ textAlign: "center" }}>Add Variant</h2>

      {/* Card search & dropdown */}
      <div style={{ position: "relative" }}>
        <input
          className="input"
          type="text"
          placeholder="Search or select a card..."
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={() => setShowDropdown(true)}
        />
        {showDropdown && searchTerm && filteredCards.length > 0 && (
          <div
            style={{
              position: "absolute",
              top: "100%",
              left: 0,
              right: 0,
              background: "#111",
              border: "1px solid rgba(255,255,255,0.1)",
              borderRadius: "6px",
              maxHeight: "200px",
              overflowY: "auto",
              zIndex: 1000,
            }}
            onMouseDown={(e) => e.preventDefault()} // keep input from blurring
          >
            {filteredCards.map((card) => (
              <div
                key={card.id}
                onClick={() => handleCardSelect(card)}
                style={{ padding: "8px 12px", cursor: "pointer", color: "#ddd" }}
              >
                {(card.cardname || card.cardName) + " "}
                <span style={{ color: "#888", fontSize: "0.85rem" }}>
                  (Cost {card.cardcost ?? card.cardCost}, Power {card.cardpower ?? card.cardPower})
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      <input
        className="input"
        type="text"
        name="cardHTML"
        placeholder="Variant Image URL"
        value={formData.cardHTML}
        onChange={(e) =>
          setFormData((prev) => ({ ...prev, cardHTML: e.target.value }))
        }
      />

      <input
        className="input"
        type="date"
        name="releaseDate"
        value={formData.releaseDate}
        onChange={(e) =>
          setFormData((prev) => ({ ...prev, releaseDate: e.target.value }))
        }
      />

      <input
        className="input"
        type="number"
        name="chanceOfGetting"
        placeholder="Chance of Getting (%)"
        value={formData.chanceOfGetting}
        onChange={(e) =>
          setFormData((prev) => ({ ...prev, chanceOfGetting: e.target.value }))
        }
      />

      {error && (
        <div style={{ color: "#f66", marginTop: 8, textAlign: "center" }}>
          {error}
        </div>
      )}

      <div style={{ textAlign: "center", marginTop: 12 }}>
        <button className="button" onClick={handleAdd} disabled={saving}>
          {saving ? "Saving..." : "Add Variant"}
        </button>
      </div>
    </div>
  );
}
